import { useAtom } from 'jotai'
import { noop } from 'lodash-es'
import { type PropsWithChildren, type ReactNode, useRef } from 'react'
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  MenuTrigger as AriaMenuTrigger,
  Popover as AriaPopover,
  RouterProvider as AriaRouterProvider,
  Separator as AriaSeparator,
  SubmenuTrigger as AriaSubmenuTrigger,
  Text as AriaText,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useHref, useNavigate } from 'react-router'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import FluentArrowExit20Regular from '~icons/fluent/arrow-exit-20-regular'
import FluentDarkTheme20Regular from '~icons/fluent/dark-theme-20-regular'
import FluentDesktop20Regular from '~icons/fluent/desktop-20-regular'
import FluentGift20Filled from '~icons/fluent/gift-20-filled'
import FluentIosChevronRight20Regular from '~icons/fluent/ios-chevron-right-20-regular'
import FluentPerson20Regular from '~icons/fluent/person-20-regular'
import FluentPersonEdit20Regular from '~icons/fluent/person-edit-20-regular'
import FluentWeatherMoon20Regular from '~icons/fluent/weather-moon-20-regular'
import FluentWeatherSunny20Regular from '~icons/fluent/weather-sunny-20-regular'
import IcRoundArrowOutward from '~icons/ic/round-arrow-outward'
import IcRoundTranslate from '~icons/ic/round-translate'
import IconParkOutlineShopping from '~icons/icon-park-outline/shopping'
import { useColorModeContext } from 'src/components/Common/ColorModeProvider'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import CreditMeter from 'src/components/Quota/CreditMeter'
import {
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import {
  SUPPORTED_LOCALES,
  type SupportedLocaleCodes,
} from 'src/i18n/constants'
import { useMyQuota } from 'src/services/apollo/quota'
import { useMe } from 'src/services/helpers/user'
import { localeAtom, updateLangPreference } from 'src/stores/intl'
import { logoutUser } from 'src/utils/auth'
import { config } from 'src/utils/config'
import { cn } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import invariant from 'src/utils/invariant'
import { startEventGroup, track } from 'src/utils/tracker'
import { getUserDisplayName, useShowEmailVerificationDot } from 'src/utils/user'
import { setLocaleToCookies } from '~/stores/locale'

// React router doesn't handle external URls properly
const useResolvedHref = (href: string) => {
  const localHref = useHref(href)
  return href.startsWith('/') ? localHref : href
}

// Reuse the render result as much as possible, since separators are literally static
const separator = (
  <AriaSeparator className="my-1 h-px bg-black/20 dark:bg-white/20" />
)

/**
 * Account menu (the app main menu)
 *
 * @param children - The menu trigger element, must be/contain a RAC pressable element
 */
export default function AccountMenu({ children }: PropsWithChildren) {
  const navigate = useNavigate()
  const { me } = useMe()
  const { t } = useTranslation('common')

  const bodyOverflowRef = useRef('')

  return (
    <AriaMenuTrigger
      onOpenChange={isOpen => {
        if (isOpen) {
          bodyOverflowRef.current = document.body.style.overflow
          // To make site header remain sticky when scroll lock is enabled
          document.body.style.overflow = 'visible'
        } else {
          document.body.style.overflow = bodyOverflowRef.current
          bodyOverflowRef.current = ''
        }
      }}
    >
      {children}
      <AriaPopover
        placement="bottom end"
        offset={4}
        crossOffset={4}
        className={popoverStyle({ animation: true })}
      >
        <AriaRouterProvider navigate={navigate} useHref={useResolvedHref}>
          <AriaMenu className={menuStyle()}>
            {me && <UserProfileButton />}
            {me && <UserSettingsButton />}

            {me && separator}

            <MenuItem
              href="/membership/plans"
              icon={<MembershipIconCircled className="size-full scale-90" />}
              onAction={() => {
                const trackingLocation = 'menu'
                track('enter_member_click', { location: trackingLocation })
                startEventGroup('member_source', trackingLocation, {
                  persistent: true,
                })
              }}
            >
              <span className="bg-gradient-to-r from-purple-500 to-fuchsia-500 dark:to-fuchsia-400 bg-clip-text text-transparent font-medium">
                <Text i18nKey="membership:membership.label" context="short" />
              </span>
            </MenuItem>
            <MenuItem
              href="/style"
              icon={<IconParkOutlineShopping className="size-full scale-90" />}
              onAction={() => {
                const trackingLocation = 'menu'
                track('shop_enter_click', { location: trackingLocation })
              }}
            >
              <span>
                <Text i18nKey="common:user-decoration.cosmetic-shop.title" />
              </span>
            </MenuItem>
            {me && (
              <MenuItem
                href="/user-referral"
                icon={
                  <FluentGift20Filled className="size-full text-amber-500" />
                }
                onAction={() => {
                  track('menu_referral_click')
                }}
              >
                <span className="bg-gradient-to-r from-amber-500 to-amber-600 dark:to-amber-400 bg-clip-text text-transparent font-medium">
                  <Text i18nKey="user:referral.menu-label" />
                </span>
              </MenuItem>
            )}

            {separator}

            <ColorThemeButton />
            <LanguageButton />

            {separator}

            <MenuItem
              href={t('common:help-center.link.href') || config.helpCenterUrl}
              target="_blank"
              onAction={() => {
                track('menu_how_to_use_click')
              }}
            >
              <div className="flex items-start">
                <Text i18nKey="common:help-center.link.label" />
                <IcRoundArrowOutward className="size-3 ml-1" />
              </div>
            </MenuItem>
            <MenuItem
              href={
                t('common:video-tutorial.link.href') || config.youtubeChannelUrl
              }
              target="_blank"
              onAction={() => {
                track('menu_video_tutorial_click')
              }}
            >
              <div className="flex items-start">
                <Text i18nKey="common:video-tutorial.link.label" />
                <IcRoundArrowOutward className="size-3 ml-1" />
              </div>
            </MenuItem>
            <MenuItem
              href="/feedback"
              onAction={() => {
                track('menu_feedback_click')
              }}
            >
              <Text i18nKey="common:feedback" />
            </MenuItem>

            {me && separator}

            {me && (
              <MenuItem
                icon={<FluentArrowExit20Regular className="size-full" />}
                onAction={() => {
                  track('logout_click', {
                    scene: 'account-button',
                  })
                  logoutUser()
                }}
              >
                <Text i18nKey="auth:logout" />
              </MenuItem>
            )}
          </AriaMenu>
        </AriaRouterProvider>
      </AriaPopover>
    </AriaMenuTrigger>
  )
}

const popoverStyle = tv({
  base: 'flex flex-col outline-none',
  variants: {
    animation: {
      true: [
        'origin-top',
        'entering:animate-in entering:fade-in entering:placement-bottom:slide-in-from-top-1 entering:placement-top:slide-in-from-bottom-1',
        'exiting:animate-out exiting:fade-out exiting:placement-bottom:slide-out-to-top-1 exiting:placement-top:slide-out-to-bottom-1',
      ],
    },
  },
})

const menuStyle = tv({
  base: [
    'group/menu',
    'flex-1 overflow-auto min-w-[150px] py-1 text-sm rounded-[10px] ring-1 shadow-xl outline-none',
    // Light
    'text-neutral-900 bg-white ring-black/10',
    // Dark
    'dark:text-neutral-100 dark:bg-zinc-900 dark:ring-white/10',
  ],
})

/*
 * ==== Menu Item ==============================================================
 */

type MenuItemProps = PropsWithChildren &
  Omit<AriaMenuItemProps, 'className' | 'children'> & {
    icon?: ReactNode
  }

function MenuItem({ icon, children, ...props }: MenuItemProps) {
  const styles = menuItemStyles({ withIcon: icon != null })

  return (
    <AriaMenuItem {...props} className={styles.root()}>
      <div className={styles.inner()}>
        <div className={styles.icon()}>{icon}</div>
        <div className={styles.label()}>{children}</div>
      </div>
    </AriaMenuItem>
  )
}

const menuItemStyles = tv({
  slots: {
    root: [
      'group/menu-item',
      'min-h-8 px-1 flex no-underline outline-none cursor-default',
    ],
    inner: [
      'flex-1 px-3 flex items-start rounded-md',
      'group-focus-visible/menu-item:outline outline-2 -outline-offset-1',
      // Checkmark
      'after:size-1.5 after:my-auto after:ml-4 after:rounded-full',
      'after:hidden group-selected/menu-item:after:block',
      // Light
      'group-focus/menu-item:text-black group-focus/menu-item:bg-black/5',
      'group-open/menu-item:text-black group-open/menu-item:bg-black/5',
      'outline-black',
      'after:bg-black',
      // Dark
      'dark:group-focus/menu-item:text-neutral-200 dark:group-focus/menu-item:bg-white/10',
      'dark:group-open/menu-item:text-neutral-200 dark:group-open/menu-item:bg-white/10',
      'outline-white',
      'dark:after:bg-white',
    ],
    icon: 'flex-none size-5 my-1.5 -ml-1 mr-2 grid place-items-center',
    label: 'flex-1 py-1.5 whitespace-nowrap',
  },
  variants: {
    withIcon: {
      false: {
        icon: 'hidden',
      },
    },
  },
})

/*
 * ==== Special Menu Items =====================================================
 */

function UserProfileButton() {
  const { me } = useMe()
  const { data: quotaData, previousData: previousQuotaData } = useMyQuota()
  const balance = quotaData ?? previousQuotaData

  // TODO: Better to render skeletons
  if (!me) return null

  return (
    <>
      <MenuItem
        href={`/@${me.username}`}
        onAction={() => {
          track('menu_profile_click')
        }}
      >
        <div className="flex-1 flex items-center">
          <UserAvatar
            user={me}
            className="flex-none mr-2.5 size-12 -translate-x-0.5"
          />
          <div className="flex-1">
            <p>{getUserDisplayName(me)}</p>
            <p className="text-xs">
              <CreditMeter value={balance} />
            </p>
          </div>
        </div>
      </MenuItem>
      <MenuItem
        href={`/@${me.username}`}
        icon={<FluentPerson20Regular className="size-full" />}
        onAction={() => {
          track('menu_profile_click', { from: 'normal-item' })
        }}
      >
        <Text i18nKey="user:your-profile-page" />
      </MenuItem>
    </>
  )
}

function UserSettingsButton() {
  const { me } = useMe()
  const [emailVerificationDot, updateDotState] = useShowEmailVerificationDot(
    'account-menu',
    me,
  )

  return (
    <MenuItem
      href={emailVerificationDot ? '/profile/edit/accounts' : '/profile/edit'}
      icon={
        <div className="relative size-full">
          <FluentPersonEdit20Regular className="size-full" />
          {emailVerificationDot && (
            <span
              aria-hidden
              className="absolute top-0 right-0 size-2 -translate-y-1/4 translate-x-1/4 aspect-square bg-[#d32f2f] rounded-full ring-[1px] ring-white dark:ring-black"
            />
          )}
        </div>
      }
      onAction={() => {
        track('edit_profile_click', { scene: 'account-button' })
        updateDotState()
      }}
    >
      <Text i18nKey="user:settings.label" />
      {emailVerificationDot && (
        <AriaText
          slot="description"
          className="block mt-0.5 text-xs text-red-600 dark:text-red-400"
        >
          <Text i18nKey="user:email.verify-for-credit-discount" />
        </AriaText>
      )}
    </MenuItem>
  )
}

function ColorThemeButton() {
  const { mode, changeColorMode } = useColorModeContext()

  return (
    <AriaSubmenuTrigger>
      <MenuItem icon={<FluentDarkTheme20Regular className="size-full" />}>
        <div className="flex-1 flex items-center">
          <span className="mr-4">
            <Text i18nKey="common:preferences.color-mode.label" />
          </span>
          <span className="ml-auto text-neutral-500">
            {match(mode)
              .with('light', () => (
                <Text i18nKey="common:preferences.color-mode.light" />
              ))
              .with('dark', () => (
                <Text i18nKey="common:preferences.color-mode.dark" />
              ))
              .with('auto', () => (
                <Text i18nKey="common:preferences.color-mode.auto" />
              ))
              .otherwise(() => null)}
          </span>
          <FluentIosChevronRight20Regular className="size-5 -mr-1" />
        </div>
      </MenuItem>
      <AriaPopover offset={-4} crossOffset={-4} className={popoverStyle()}>
        <AriaMenu
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={mode && [mode]}
          onSelectionChange={key => {
            invariant(key instanceof Set, 'The selection should be a Set')
            // TODO: remove as any when this type issue can be resolved
            const value = key.values().next().value as any
            track('color_mode_select', { value })
            changeColorMode(value)
          }}
          className={menuStyle()}
        >
          <MenuItem
            id="light"
            icon={
              <FluentWeatherSunny20Regular className="size-full scale-125" />
            }
          >
            <Text i18nKey="common:preferences.color-mode.light" />
          </MenuItem>
          <MenuItem
            id="dark"
            icon={<FluentWeatherMoon20Regular className="size-full scale-90" />}
          >
            <Text i18nKey="common:preferences.color-mode.dark" />
          </MenuItem>
          <MenuItem
            id="auto"
            icon={<FluentDesktop20Regular className="size-full scale-95" />}
          >
            <Text i18nKey="common:preferences.color-mode.auto" />
          </MenuItem>
        </AriaMenu>
      </AriaPopover>
    </AriaSubmenuTrigger>
  )
}

function LanguageButton() {
  const [locale, setLocale] = useAtom(localeAtom)
  const { t } = useTranslation('common')

  const isMobile = useIsMobile()

  const handleLanguageChange = (value: SupportedLocaleCodes) => {
    track('menu_language_click', { value })
    setLocaleToCookies(value)
    updateLangPreference(value).catch(noop)
    setLocale(value)
  }

  const openLanguageDialog = () => {
    UIDialog.create({
      responsive: true,
      dismissable: true,
      className: 'max-w-none',
      children: ({ close }) => (
        <>
          <UIDialogHeader heading={t('language')} />
          <UIDialogContent className="flex flex-col gap-1 px-2">
            {Object.values(SUPPORTED_LOCALES).map(item => (
              <button
                type="button"
                key={item.code}
                className={cn(
                  'flex items-center px-4 py-3 rounded-lg text-left',
                  'hover:bg-black/5 dark:hover:bg-white/10',
                  locale === item.code && 'bg-black/5 dark:bg-white/10',
                )}
                onClick={() => {
                  handleLanguageChange(item.code)
                  close()
                }}
              >
                <span className="flex-1">{item.label}</span>
                {locale === item.code && (
                  <span className="size-2 rounded-full bg-black dark:bg-white" />
                )}
              </button>
            ))}
          </UIDialogContent>
        </>
      ),
    })
  }

  if (isMobile) {
    return (
      <MenuItem
        icon={<IcRoundTranslate className="size-full scale-90" />}
        onAction={openLanguageDialog}
      >
        <div className="flex-1 flex items-center">
          <span className="mr-4">
            <Text i18nKey="common:language" />
          </span>
          <span className="ml-auto text-neutral-500">
            {SUPPORTED_LOCALES[locale].label}
          </span>
          <FluentIosChevronRight20Regular className="size-5 -mr-1" />
        </div>
      </MenuItem>
    )
  }

  return (
    <AriaSubmenuTrigger>
      <MenuItem icon={<IcRoundTranslate className="size-full scale-90" />}>
        <div className="flex-1 flex items-center">
          <span className="mr-4">
            <Text i18nKey="common:language" />
          </span>
          <span className="ml-auto text-neutral-500">
            {SUPPORTED_LOCALES[locale].label}
          </span>
          <FluentIosChevronRight20Regular className="size-5 -mr-1" />
        </div>
      </MenuItem>
      <AriaPopover offset={-4} crossOffset={-4} className={popoverStyle()}>
        <AriaMenu
          items={Object.values(SUPPORTED_LOCALES)}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[locale]}
          onSelectionChange={key => {
            invariant(key instanceof Set, 'The selection should be a Set')
            // TODO: remove as any when this type issue can be resolved
            const value = key.values().next().value as any
            handleLanguageChange(value)
          }}
          className={menuStyle()}
        >
          {item => <MenuItem id={item.code}>{item.label}</MenuItem>}
        </AriaMenu>
      </AriaPopover>
    </AriaSubmenuTrigger>
  )
}
